import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api';

/**
 * Shared shell for the two public guide pages (creator-prep, audience-prep).
 * Handles: brand header, footer, page-level typography, back-link,
 * and the 🌐 12-language translation dropdown.
 *
 * Translation strategy — DOM-text-walker.
 *   The guide bodies are heavy JSX (paragraphs, lists, callouts, figures)
 *   and refactoring every string into a keyed manifest would be intrusive
 *   and fragile. Instead, after mount we walk every visible text node
 *   under `.guide-hero` + `.guide-body`, capture its original English
 *   value into a WeakMap, and — when the user picks a language — POST
 *   the whole batch to /api/guides/translate and swap text nodes in
 *   place. Selecting "English" restores originals from the WeakMap.
 *   The backend caches every translation permanently, so the second
 *   time a language is opened it's near-instant.
 *
 *   We skip:
 *     • text inside `<a>` tags where the text IS the URL
 *     • elements marked `.no-translate` (share URLs, code, brand marks)
 *     • whitespace-only text nodes
 */
const SUPPORTED_LANGS = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'pt', name: 'Português', flag: '🇧🇷' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'ja', name: '日本語', flag: '🇯🇵' },
  { code: 'ko', name: '한국어', flag: '🇰🇷' },
  { code: 'zh', name: '简体中文', flag: '🇨🇳' },
  { code: 'hi', name: 'हिन्दी', flag: '🇮🇳' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦' },
  { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
];

// Skip these tags entirely — we don't want to translate URLs, code,
// or content the author explicitly marked as untranslatable.
const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'CODE', 'PRE', 'INPUT', 'TEXTAREA']);

function shouldSkipNode(node) {
  let p = node.parentElement;
  while (p) {
    if (SKIP_TAGS.has(p.tagName)) return true;
    if (p.classList && p.classList.contains('no-translate')) return true;
    // Anchor tags where visible text is a URL (share links etc.)
    if (p.tagName === 'A' && /^https?:\/\//.test((node.textContent || '').trim())) return true;
    p = p.parentElement;
  }
  return false;
}

function collectTextNodes(root) {
  const nodes = [];
  if (!root) return nodes;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(n) {
      const raw = n.nodeValue;
      if (!raw || !raw.trim()) return NodeFilter.FILTER_REJECT;
      if (shouldSkipNode(n)) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  let n = walker.nextNode();
  while (n) {
    nodes.push(n);
    n = walker.nextNode();
  }
  return nodes;
}

export default function GuideShell({ eyebrow, title, subtitle, accent = 'creator', slug, children }) {
  const [lang, setLang] = useState(() => {
    try { return localStorage.getItem('clanster_guide_lang') || 'en'; } catch (_) { return 'en'; }
  });
  const [translating, setTranslating] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const rootRef = useRef(null);
  // WeakMap: DOM text-node → original English string. Populated on first
  // language switch and used to restore when the user picks English again.
  const originalsRef = useRef(new WeakMap());
  // Per-language cache (keyed by node index) so re-selecting a language
  // doesn't need a re-round-trip within the same page load.
  const translatedByLangRef = useRef({});

  useEffect(() => {
    document.title = `${title} · Clanster`;
    return () => { document.title = 'Clanster'; };
  }, [title]);

  useEffect(() => {
    try { localStorage.setItem('clanster_guide_lang', lang); } catch (_) {}
  }, [lang]);

  const applyLang = useCallback(async (nextLang) => {
    setMenuOpen(false);
    if (nextLang === lang) return;
    const root = rootRef.current;
    if (!root) return;
    const nodes = collectTextNodes(root);
    if (!nodes.length) { setLang(nextLang); return; }

    // Capture originals the first time we touch each node.
    const wm = originalsRef.current;
    nodes.forEach((n) => {
      if (!wm.has(n)) wm.set(n, n.nodeValue);
    });

    if (nextLang === 'en') {
      nodes.forEach((n) => { n.nodeValue = wm.get(n); });
      setLang('en');
      return;
    }

    // Session-cache hit — apply immediately, no network.
    const sess = translatedByLangRef.current[nextLang];
    if (sess) {
      nodes.forEach((n, i) => {
        const t = sess[String(i)];
        if (typeof t === 'string' && t.trim()) n.nodeValue = t;
      });
      setLang(nextLang);
      return;
    }

    setTranslating(true);
    const keys = {};
    nodes.forEach((n, i) => { keys[String(i)] = wm.get(n); });
    try {
      const res = await api.post('/guides/translate', { slug: slug || 'guide', lang: nextLang, keys });
      const translated = res.data?.keys || {};
      translatedByLangRef.current[nextLang] = translated;
      nodes.forEach((n, i) => {
        const t = translated[String(i)];
        if (typeof t === 'string' && t.trim()) n.nodeValue = t;
      });
      setLang(nextLang);
    } catch (err) {
      // Keep English on failure — never leave the page half-translated.
      console.warn('[guide] translation failed', err);
      alert('Translation failed — please try again in a moment.');
    } finally {
      setTranslating(false);
    }
  }, [lang, slug]);

  const currentLang = SUPPORTED_LANGS.find((l) => l.code === lang) || SUPPORTED_LANGS[0];

  return (
    <div className={`guide-page guide-accent-${accent}`} ref={rootRef}>
      <header className="guide-header">
        <Link to="/" className="guide-logo no-translate" data-testid="guide-back-home">
          <img src="/logo.png" alt="" className="guide-logo-img" width="28" height="28" />
          <span>Clanster</span>
        </Link>
        <div className="guide-header-links">
          <div className="guide-lang-wrap no-translate" data-testid="guide-lang-wrap">
            <button
              type="button"
              className={`guide-lang-btn${translating ? ' is-loading' : ''}`}
              onClick={() => setMenuOpen((v) => !v)}
              aria-haspopup="listbox"
              aria-expanded={menuOpen}
              disabled={translating}
              data-testid="guide-lang-btn"
              title="Translate this page"
            >
              <span aria-hidden="true">🌐</span>
              <span className="guide-lang-current">{currentLang.flag} {currentLang.name}</span>
              <span aria-hidden="true" className="guide-lang-chev">▾</span>
            </button>
            {menuOpen && (
              <ul className="guide-lang-menu" role="listbox" data-testid="guide-lang-menu">
                {SUPPORTED_LANGS.map((l) => (
                  <li key={l.code} role="option" aria-selected={l.code === lang}>
                    <button
                      type="button"
                      className={`guide-lang-item${l.code === lang ? ' is-selected' : ''}`}
                      onClick={() => applyLang(l.code)}
                      data-testid={`guide-lang-item-${l.code}`}
                    >
                      <span className="guide-lang-flag">{l.flag}</span>
                      <span>{l.name}</span>
                      {l.code === lang && <span aria-hidden="true" className="guide-lang-tick">✓</span>}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {translating && (
              <div className="guide-lang-status" data-testid="guide-lang-status">
                Translating…
              </div>
            )}
          </div>
          <Link to="/creators" className="no-translate" data-testid="guide-header-creators">For creators</Link>
          <Link to="/signup" className="guide-header-cta no-translate" data-testid="guide-header-signup">
            Sign up
          </Link>
        </div>
      </header>

      <main className="guide-main">
        <section className="guide-hero">
          <div className="guide-eyebrow">{eyebrow}</div>
          <h1 className="guide-title">{title}</h1>
          <p className="guide-subtitle">{subtitle}</p>
        </section>

        <section className="guide-body">
          {children}
        </section>
      </main>

      <footer className="guide-footer">
        <div className="guide-footer-inner no-translate">
          © {new Date().getFullYear()} Clanster · <Link to="/help">Help</Link> ·{' '}
          <a href="mailto:support@clanster.xyz">support@clanster.xyz</a>
        </div>
      </footer>
    </div>
  );
}

export function StepCard({ number, title, children }) {
  return (
    <div className="guide-step" data-testid={`guide-step-${number}`}>
      <div className="guide-step-num no-translate">{number}</div>
      <div className="guide-step-body">
        <h2 className="guide-step-title">{title}</h2>
        <div className="guide-step-content">{children}</div>
      </div>
    </div>
  );
}

export function SectionHeader({ eyebrow, title, kicker }) {
  // Big section divider for grouping steps into A / B / C / D within one
  // guide. Only the Audience Guide uses this currently.
  return (
    <div className="guide-section-header" data-testid={`guide-section-${(eyebrow || title || '').toLowerCase().replace(/\s+/g, '-')}`}>
      {eyebrow && <div className="guide-section-eyebrow">{eyebrow}</div>}
      <h2 className="guide-section-title">{title}</h2>
      {kicker && <p className="guide-section-kicker">{kicker}</p>}
    </div>
  );
}

export function TipCallout({ tone = 'info', children }) {
  return <div className={`guide-callout guide-callout-${tone}`}>{children}</div>;
}

export function InfoRow({ children }) {
  return <div className="guide-inforow">{children}</div>;
}
